import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
} from '@mui/material';
import { usePermissionCheck } from '../../hooks/usePermission';

type Props = {
	open: boolean;
	onClose: () => void;
	permission: string;
	action: string;
};

export default function PermissionDialog({ open, onClose, permission, action }: Props) {
	const result = usePermissionCheck(permission, action);

	const prettyPermission = formatPermissionName(action);

	const getDescription = () => {
		if (!result) return 'Checking permissions…';

		switch (result.type) {
			case 'auto':
				return (
					<>
						<Typography gutterBottom>
							✅ You have both <b>maker</b> and <b>checker</b> rights for this permission.
						</Typography>
						<Typography>
							Your attempt to perform <b>{prettyPermission}</b> ({action}) will be{' '}
							<b>committed immediately</b> without requiring additional approval.
						</Typography>
					</>
				);
			case 'maker-checker':
				return (
					<>
						<Typography gutterBottom>
							⚠️ You only have <b>maker</b> rights for this permission.
						</Typography>
						<Typography>
							Your attempt to perform <b>{prettyPermission}</b> ({action}) will be{' '}
							<b>recorded as pending</b>. A user with checker rights must review and approve it
							before it is finalized.
						</Typography>
					</>
				);
			case 'denied':
				return (
					<>
						<Typography gutterBottom>
							❌ You don’t have rights for <b>{prettyPermission}</b> ({action}).
						</Typography>
						<Typography>
							Please contact your administrator if you believe you should have access.
						</Typography>
					</>
				);
			default:
				return 'Checking permissions…';
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Maker–Checker Approval</DialogTitle>
			<DialogContent>
				<Box>{getDescription()}</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="contained" color="primary" autoFocus>
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function formatPermissionName(permission: string) {
	if (!permission) return '';
	return permission
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, c => c.toUpperCase());
}
